/**
 * src/api/auth.ts
 * ----------------
 * API functions for Helix customer authentication.
 *
 * Fully implemented this will:
 *   - signIn(email, password) — POST /auth/sign-in — returns { accessToken, refreshToken, user }
 *   - signUp(email, password, displayName) — POST /auth/sign-up
 *   - signOut() — POST /auth/sign-out — invalidates the refresh token server-side
 *   - refreshAccessToken(refreshToken) — POST /auth/refresh — silent token rotation
 *   - requestPasswordReset(email) — POST /auth/password-reset/request
 *   - confirmPasswordReset(token, newPassword) — POST /auth/password-reset/confirm
 *   - getCurrentUser() — GET /auth/me — returns the authenticated CustomerUser
 *
 * JWT strategy:
 *   - Access token: short-lived (15 min), stored in memory (auth store)
 *   - Refresh token: long-lived (30 days), stored in httpOnly cookie set by backend
 *   - The API client interceptor calls refreshAccessToken() automatically on 401.
 *
 * Key dependencies:
 *   src/api/client.ts, src/types/auth.ts
 */
import apiClient from './client'
import { getCurrentOrgId } from '@/lib/org'
import type { AuthResponse, CustomerUser } from '@/types/auth'

type BackendCustomerUser = {
  id: string
  email?: string
  display_name?: string
  displayName?: string
  avatar_url?: string | null
  avatarUrl?: string | null
  locale?: string
  created_at?: string
  createdAt?: string
  crm_customer_id?: string | null
  crmCustomerId?: string | null
  metadata?: Record<string, unknown>
}

type BackendAuthResponse = {
  access_token?: string
  accessToken?: string
  expires_in?: number
  expiresIn?: number
  user: BackendCustomerUser
}

function normalizeUser(user: BackendCustomerUser): CustomerUser {
  return {
    id: user.id,
    email: user.email ?? '',
    displayName: user.displayName ?? user.display_name ?? 'Customer',
    avatarUrl: user.avatarUrl ?? user.avatar_url ?? undefined,
    locale: user.locale ?? 'en',
    createdAt: user.createdAt ?? user.created_at ?? new Date().toISOString(),
    crmCustomerId: user.crmCustomerId ?? user.crm_customer_id ?? undefined,
    metadata: user.metadata,
  }
}

function normalizeAuth(data: BackendAuthResponse): AuthResponse {
  return {
    accessToken: data.accessToken ?? data.access_token ?? '',
    expiresIn: data.expiresIn ?? data.expires_in ?? 0,
    user: normalizeUser(data.user),
  }
}

/** Sign in with email and password. */
export async function signIn(
  email: string,
  password: string,
): Promise<AuthResponse> {
  const { data } = await apiClient.post<BackendAuthResponse>('/auth/sign-in', {
    email,
    password,
    org_id: getCurrentOrgId(),
  })
  return normalizeAuth(data)
}

/** Create a new customer account. */
export async function signUp(
  email: string,
  password: string,
  displayName: string,
): Promise<AuthResponse> {
  const { data } = await apiClient.post<BackendAuthResponse>('/auth/sign-up', {
    email,
    password,
    display_name: displayName,
    org_id: getCurrentOrgId(),
  })
  return normalizeAuth(data)
}

/** Sign out the current user (invalidates server-side refresh token). */
export async function signOut(): Promise<void> {
  await apiClient.post('/auth/sign-out')
}

/** Silently refresh the access token using the httpOnly refresh cookie. */
export async function refreshAccessToken(): Promise<{ accessToken: string }> {
  const { data } = await apiClient.post<{ access_token: string }>(
    '/auth/refresh',
  )
  return { accessToken: data.access_token }
}

/** Fetch the currently authenticated user profile. */
export async function getCurrentUser(): Promise<CustomerUser> {
  const { data } = await apiClient.get<BackendCustomerUser>('/auth/me')
  return normalizeUser(data)
}

/** Request a password-reset email. */
export async function requestPasswordReset(email: string): Promise<void> {
  await apiClient.post('/auth/password-reset/request', { email })
}

/** Confirm a password reset with the token from the email link. */
export async function confirmPasswordReset(
  token: string,
  newPassword: string,
): Promise<void> {
  await apiClient.post('/auth/password-reset/confirm', {
    token,
    new_password: newPassword,
  })
}
